import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  afterRenderEffect,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TuiButton, TuiDataList, TuiDropdown, TuiIcon } from '@taiga-ui/core';
import { TuiChevron } from '@taiga-ui/kit';
import { TuiNavigation } from '@taiga-ui/layout';
import { TuiActiveZone } from '@taiga-ui/cdk';
import { SeasonStoreService } from '../../../features/seasons/services/season-store.service';
import { SportStoreService } from '../../../features/sports/services/sport-store.service';
import { AuthButtonsComponent } from '../../../features/auth/components/auth-buttons/auth-buttons.component';
import { AuthService } from '../../../features/auth/services/auth.service';
import { PermissionsService } from '../../../core/auth/permissions.service';
import { ThemeService } from '../../services/theme.service';
import { NavItem, OverflowGroup, fitCount } from './nav-overflow';

/** Must match the gap on [tuiNavigationSegments] in navbar.component.less. */
const SEGMENT_GAP_PX = 8;

@Component({
  selector: 'app-navbar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    RouterLinkActive,
    TuiNavigation,
    TuiButton,
    TuiIcon,
    TuiDropdown,
    TuiChevron,
    TuiDataList,
    TuiActiveZone,
    AuthButtonsComponent,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.less',
})
export class NavbarComponent {
  private readonly seasonStore = inject(SeasonStoreService);
  private readonly sportStore = inject(SportStoreService);
  private readonly themeService = inject(ThemeService);
  private readonly authService = inject(AuthService);
  private readonly permissions = inject(PermissionsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly isAuthenticated = this.authService.isAuthenticated;
  /** Playbooks is an admin-only authoring area; hidden from non-admins in the nav. */
  readonly isAdmin = this.permissions.isAdmin;

  readonly title = 'statsboard';
  readonly sports = this.sportStore.sports;
  readonly seasons = this.seasonStore.seasons;
  readonly currentTheme = this.themeService.currentTheme;

  /** Bar order = importance order; overflow collapses from the end. */
  private readonly baseItems: NavItem[] = [
    {
      id: 'my-team',
      label: 'My Team',
      authOnly: true,
      children: [
        { label: 'My Teams', route: '/my-teams' },
        { label: 'Find a Team', route: '/find-team' },
      ],
    },
    { id: 'sports', label: 'Sports', children: [] },
    { id: 'persons', label: 'Persons', route: '/persons', menuGroup: 'Directory' },
    { id: 'stadiums', label: 'Stadiums', route: '/stadiums', menuGroup: 'Directory' },
    {
      id: 'sponsors',
      label: 'Sponsors',
      children: [
        { label: 'Sponsors', route: '/sponsors' },
        { label: 'Sponsor Lines', route: '/sponsor-lines' },
      ],
    },
    {
      id: 'equipment',
      label: 'Equipment',
      children: [
        { label: 'Weapons', route: '/weapons' },
        { label: 'Scopes', route: '/scopes' },
        { label: 'Bullets', route: '/bullets' },
        { label: 'Manufacturers', route: '/weapon-manufacturers' },
      ],
    },
    { id: 'playbooks', label: 'Playbooks', route: '/playbooks', adminOnly: true, menuGroup: 'Admin' },
    {
      id: 'presets',
      label: 'Presets',
      route: '/sport-scoreboard-presets',
      menuGroup: 'Admin',
      menuLabel: 'Scoreboard Presets',
    },
  ];

  readonly eligibleItems = computed<NavItem[]>(() => {
    const authed = this.isAuthenticated();
    const admin = this.isAdmin();
    return this.baseItems
      .filter((i) => (!i.authOnly || authed) && (!i.adminOnly || admin))
      .map((i) =>
        i.id === 'sports'
          ? { ...i, children: this.sports().map((s) => ({ label: s.title, route: `/sports/${s.id}` })) }
          : i,
      );
  });

  /**
   * Measured layout inputs. Public and writable so component tests can inject
   * widths — jsdom has no layout, so offsetWidth is always 0 there.
   */
  readonly itemWidths = signal<number[]>([]);
  readonly moreWidth = signal(0);
  /** null = not measured yet; 0 is a real measurement meaning "no space". */
  readonly containerWidth = signal<number | null>(null);

  /** How many leading items render in the bar; the rest go to the overflow menu. */
  readonly visibleCount = computed(() => {
    const widths = this.itemWidths();
    const container = this.containerWidth();
    if (widths.length === 0) {
      return this.eligibleItems().length;
    }
    // Unmeasured (initial render, hidden ancestor): show all — avoids a
    // flash of the collapsed state before real measurements arrive. A
    // measured 0 is NOT unmeasured: a phone-width bar with no room must
    // collapse to the menu, not show everything.
    if (container === null || widths.some((w) => w <= 0)) {
      return widths.length;
    }
    return fitCount(widths, container, this.moreWidth(), SEGMENT_GAP_PX);
  });

  readonly showMore = computed(
    () => this.visibleCount() > 0 && this.visibleCount() < this.eligibleItems().length,
  );
  readonly showMenuButton = computed(
    () => this.eligibleItems().length > 0 && this.visibleCount() === 0,
  );

  /**
   * False until the first real measurement lands. The template keeps items
   * invisible (but measurable) during this window, so the bar never paints
   * the show-everything fallback and then visibly collapses ("dancing").
   */
  readonly measured = computed(
    () => this.containerWidth() !== null && this.itemWidths().length > 0,
  );

  readonly overflowGroups = computed<OverflowGroup[]>(() => {
    const groups: OverflowGroup[] = [];
    for (const item of this.eligibleItems().slice(this.visibleCount())) {
      if (item.children) {
        groups.push({ label: item.label, entries: item.children });
        continue;
      }
      const label = item.menuGroup ?? 'Navigation';
      let group = groups.find((g) => g.label === label);
      if (!group) {
        group = { label, entries: [] };
        groups.push(group);
      }
      group.entries.push({ label: item.menuLabel ?? item.label, route: item.route ?? '/' });
    }
    return groups;
  });

  readonly openItemId = signal<string | null>(null);
  readonly moreOpen = signal(false);

  private readonly segmentsEl = viewChild.required<ElementRef<HTMLElement>>('segments');
  /**
   * The measured elements are the buttons/links, not their .nav-item wrappers.
   * Taiga's `[tuiNavigationSegments] > *` rule gives every wrapper
   * `flex: 1 0 auto`, so a wrapper's width depends on how many items are
   * currently in flow — measuring it feeds the fit its own output and the
   * count oscillates until Angular aborts with NG0103 (FAS-5iq1). The controls
   * themselves are `flex: 0 0 auto` and measure the same whether their wrapper
   * is in flow or overflowed (absolute). `read: ElementRef` because TuiButton
   * is a component, so a bare template ref would resolve to its instance.
   */
  private readonly itemEls = viewChildren('navItem', { read: ElementRef<HTMLElement> });
  private readonly moreEl = viewChild('moreBtn', { read: ElementRef<HTMLElement> });

  private lastMoreAnchor: 'more' | 'menu' | null = null;

  constructor() {
    // A dropdown whose anchor overflowed (or un-overflowed) must not silently
    // reopen later: clear open state whenever its anchor stops being active.
    effect(() => {
      const count = this.visibleCount();
      const openId = this.openItemId();
      if (openId !== null) {
        const idx = this.eligibleItems().findIndex((i) => i.id === openId);
        if (idx === -1 || idx >= count) {
          this.openItemId.set(null);
        }
      }

      const anchor = this.showMore() ? 'more' : this.showMenuButton() ? 'menu' : null;
      if (this.moreOpen() && anchor !== this.lastMoreAnchor) {
        this.moreOpen.set(false);
      }
      this.lastMoreAnchor = anchor;
    });

    afterNextRender(() => {
      const el = this.segmentsEl().nativeElement;
      // A zero reading means layout hasn't happened (or jsdom) — keep the
      // "unmeasured" state instead of clobbering a real or test-set width.
      if (el.clientWidth > 0) {
        this.containerWidth.set(el.clientWidth);
      }
      if (typeof ResizeObserver === 'undefined') {
        return; // jsdom — tests drive containerWidth directly
      }
      const observer = new ResizeObserver((entries) => {
        this.containerWidth.set(Math.floor(entries[entries.length - 1].contentRect.width));
      });
      observer.observe(el);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });

    // Re-measures whenever the rendered item set changes (login/logout,
    // admin state, sports list load). Overflowed items keep real widths
    // because they are hidden with visibility, not display — and because the
    // measured controls (see itemEls) never stretch with the bar.
    afterRenderEffect(() => {
      this.measureItems();
    });

    // A web-font swap changes item widths without adding or removing DOM
    // nodes, which the render effect can't see — re-measure once fonts load.
    afterNextRender(() => {
      document.fonts?.ready.then(() => this.measureItems()).catch(() => {});
    });
  }

  private measureItems(): void {
    const widths = this.itemEls().map((el) => el.nativeElement.offsetWidth);
    if (widths.length > 0 && widths.every((w) => w > 0) && widths.join() !== this.itemWidths().join()) {
      this.itemWidths.set(widths);
    }
    const more = this.moreEl()?.nativeElement.offsetWidth ?? 0;
    if (more > 0 && more !== this.moreWidth()) {
      this.moreWidth.set(more);
    }
  }

  toggleItem(id: string): void {
    this.openItemId.update((open) => (open === id ? null : id));
  }

  onItemActiveZone(id: string, active: boolean): void {
    if (!active && this.openItemId() === id) {
      this.openItemId.set(null);
    }
  }

  toggleMore(): void {
    this.moreOpen.update((v) => !v);
  }

  onMoreActiveZone(active: boolean): void {
    if (!active) {
      this.moreOpen.set(false);
    }
  }

  closeMore(): void {
    this.moreOpen.set(false);
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  getThemeIcon(): string {
    return this.currentTheme() === 'dark' ? '@tui.sun' : '@tui.moon';
  }
}
